import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, User, FileText, Upload } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

export default function OfficeCreateClientRequest() {
    const [officeData, setOfficeData] = useState(null);
    const [clients, setClients] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        client_id: "",
        lawyer_id: "",
        title: "",
        description: "",
        case_type_id: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                Swal.fire("Error", "Authentication required.", "error");
                setLoading(false);
                return;
            }

            try {
                const [officeRes, clientsRes, caseTypesRes] = await Promise.all([
                    api.get("/offices", { headers: { Authorization: `Bearer ${token}` } }),
                    api.get("/clients", { headers: { Authorization: `Bearer ${token}` } }),
                    api.get("/case-types", { headers: { Authorization: `Bearer ${token}` } }),
                ]);

                if (officeRes.data.success) {
                    setOfficeData(officeRes.data.data);
                }

                if (clientsRes.data.success) {
                    setClients(clientsRes.data.data || []);
                }

                if (caseTypesRes.data.success) {
                    const activeTypes = (caseTypesRes.data.data || []).filter(ct => ct.is_active);
                    setCaseTypes(activeTypes);
                }
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to load data.";
                Swal.fire("Error", msg, "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const token = localStorage.getItem("auth_token");
        if (!token) {
            Swal.fire("Error", "Authentication required.", "error");
            setSaving(false);
            return;
        }

        const payload = new FormData();
        payload.append("client_id", formData.client_id);
        payload.append("lawyer_id", formData.lawyer_id);
        payload.append("title", formData.title.trim());
        payload.append("description", formData.description.trim());
        payload.append("case_type_id", formData.case_type_id);

        const files = fileInputRef.current?.files;
        if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                payload.append("files[]", files[i]);
            }
        }

        try {
            const res = await api.post("/requests/for-client", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.data.success) {
                Swal.fire({
                    icon: "success",
                    title: "Request Created!",
                    text: "The consultation request has been sent to the client.",
                    confirmButtonColor: "#1e40af",
                    timer: 4000,
                    timerProgressBar: true,
                });

                // Reset form
                setFormData({
                    client_id: "",
                    lawyer_id: "",
                    title: "",
                    description: "",
                    case_type_id: "",
                });
                if (fileInputRef.current) fileInputRef.current.value = "";
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create request.";
            Swal.fire("Error", msg, "error");
        } finally {
            setSaving(false);
        }
    };

    // استخراج المحامين فقط
    const lawyers = officeData?.members?.filter(m => m.role === "lawyer" && m.is_active) || [];

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <section className="p-6 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Create Client Request</h2>
                    <p className="text-gray-600 mt-1">Submit a consultation request on behalf of a client</p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                >
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Client Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <User className="w-4 h-4 inline mr-1" />
                                Select Client *
                            </label>
                            <select
                                required
                                value={formData.client_id}
                                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Choose a client...</option>
                                {clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.name} ({client.email})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Lawyer Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <User className="w-4 h-4 inline mr-1" />
                                Assign to Lawyer *
                            </label>
                            <select
                                required
                                value={formData.lawyer_id}
                                onChange={(e) => setFormData({ ...formData, lawyer_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Choose a lawyer...</option>
                                {lawyers.map((member) => (
                                    <option key={member.user.id} value={member.user.id}>
                                        {member.user.name} ({member.user.email})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Case Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <FileText className="w-4 h-4 inline mr-1" />
                                Case Type *
                            </label>
                            <select
                                required
                                value={formData.case_type_id}
                                onChange={(e) => setFormData({ ...formData, case_type_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Choose a case type...</option>
                                {caseTypes.map((ct) => (
                                    <option key={ct.id} value={ct.id}>
                                        {ct.name} - {parseFloat(ct.price).toFixed(2)} USD
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Request Title *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="e.g., Divorce Consultation"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description *
                            </label>
                            <textarea
                                required
                                rows={4}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Provide details about the case..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Files */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Upload className="w-4 h-4 inline mr-1" />
                                Attach Files (Optional)
                            </label>
                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">You can upload multiple documents (PDF, images, etc.)</p>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData({
                                        client_id: "",
                                        lawyer_id: "",
                                        title: "",
                                        description: "",
                                        case_type_id: "",
                                    });
                                    if (fileInputRef.current) fileInputRef.current.value = "";
                                }}
                                className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                            >
                                Clear
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className={`px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-70`}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <FileText className="w-4 h-4" />
                                        Create Request
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </section>
    );
}